import { PlaybookRun, PlaybookTask, PlaybookTemplate, TaskGroup } from "@/types/customer-success"

// Helper function to generate UUIDs
const generateUUID = () => crypto.randomUUID();

// Helper function to gerar datas futuras
const getFutureDate = (daysFromNow: number) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

// Mock Email Templates
export const mockEmailTemplates = [
  {
    id: "email_template_1",
    name: "Boas-vindas ao Cliente",
    subject: "Bem-vindo à nossa plataforma!",
    description: "E-mail de boas-vindas enviado após a conclusão do onboarding",
    category: "onboarding",
    created_at: "2023-05-10T14:30:00Z",
    updated_at: "2023-06-15T09:45:00Z"
  },
  {
    id: "email_template_2",
    name: "Materiais de Treinamento",
    subject: "Seus materiais de treinamento estão prontos",
    description: "E-mail com links para materiais de treinamento e recursos de aprendizado",
    category: "training",
    created_at: "2023-04-22T11:20:00Z",
    updated_at: "2023-07-05T16:30:00Z"
  },
  {
    id: "email_template_3",
    name: "Confirmação de Configuração",
    subject: "Sua configuração foi concluída com sucesso",
    description: "Confirmação de que a configuração da conta foi concluída",
    category: "setup",
    created_at: "2023-03-18T10:15:00Z",
    updated_at: "2023-05-20T13:40:00Z"
  },
  {
    id: "email_template_4",
    name: "Lembrete de Reunião",
    subject: "Lembrete: Nossa reunião está agendada para amanhã",
    description: "Lembrete de reunião com detalhes e agenda",
    category: "meeting",
    created_at: "2023-06-30T09:00:00Z",
    updated_at: "2023-06-30T09:00:00Z"
  },
  {
    id: "email_template_5",
    name: "Relatório Mensal",
    subject: "Seu relatório mensal está disponível",
    description: "E-mail informando sobre a disponibilidade do relatório mensal",
    category: "report",
    created_at: "2023-02-05T14:20:00Z",
    updated_at: "2023-08-10T11:35:00Z"
  }
];

// Mock Subscription data
export const mockSubscription = {
  id: "sub_123456",
  customer_tenant_id: "tenant_789",
  state: "active",
  start_date: "2023-01-15",
  end_date: "2024-01-14",
  currency_code: "BRL",
  auto_renew: true,
  customer_tenant: {
    name: "WEISS & COMPANY"
  },
  items: [
    {
      id: "item_1",
      subscription_plan: {
        name: "Enterprise Plan",
        billing_period_type: "yearly"
      },
      quantity: 1,
      unit_price: 12000
    },
    {
      id: "item_2",
      subscription_plan: {
        name: "Additional Users",
        billing_period_type: "yearly"
      },
      quantity: 5,
      unit_price: 1200
    },
    {
      id: "item_3",
      subscription_plan: {
        name: "Premium Support",
        billing_period_type: "yearly"
      },
      quantity: 1,
      unit_price: 3600
    },
    {
      id: "item_4",
      subscription_plan: {
        name: "API Access",
        billing_period_type: "yearly"
      },
      quantity: 1,
      unit_price: 2400
    }
  ]
};

// Mock Playbook Templates
export const mockPlaybookTemplates: PlaybookTemplate[] = [
  {
    id: generateUUID(),
    subscription_id: "sub_123456",
    name: "Client Onboarding",
    description: "Standard onboarding process for new clients",
    template_content: JSON.stringify({
      groups: [
        {
          title: "Getting started",
          icon: "🎯",
          tasks: [
            { title: "Sign your contract", task_type: "task" },
            { title: "Schedule your kickoff call", task_type: "task", description: "Invite anyone who will be using the product on a daily basis, so we can get alignment..." },
            { title: "Tell us more about your account setup", task_type: "task" },
            { title: "Capture onboarding goals", task_type: "task", description: "Take a moment to capture why you purchased so we can stay aligned throughout..." },
            { title: "Complete account setup", task_type: "automated" }
          ]
        },
        {
          title: "Implementation",
          icon: "🔧",
          tasks: [
            { title: "Configure your workspace", task_type: "task" },
            { title: "Import your data", task_type: "automated" },
            { title: "Set up user permissions", task_type: "task" },
            { title: "Configure integrations", task_type: "task" }
          ]
        },
        {
          title: "Training",
          icon: "📚",
          tasks: [
            { title: "Admin training session", task_type: "task" },
            { title: "User training session", task_type: "task" },
            { title: "Complete training assessment", task_type: "task" }
          ]
        },
        {
          title: "Go-Live",
          icon: "🚀",
          tasks: [
            { title: "Final review meeting", task_type: "task" },
            { title: "Go-live approval", task_type: "approval" },
            { title: "Post-launch check-in", task_type: "task" },
            { title: "Send welcome email", task_type: "email_draft" },
            { title: "Send automatic follow-up", task_type: "email_auto_send" }
          ]
        }
      ]
    }),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: "user_123",
    updated_by: "user_123"
  },
  {
    id: generateUUID(),
    subscription_id: "sub_123456",
    name: "Quarterly Business Review",
    description: "Standard process for quarterly business reviews with clients",
    template_content: JSON.stringify({
      groups: [
        {
          title: "Preparation",
          icon: "📋",
          tasks: [
            { title: "Gather usage metrics", task_type: "automated" },
            { title: "Prepare performance report", task_type: "staff" },
            { title: "Identify growth opportunities", task_type: "staff" },
            { title: "Schedule QBR meeting", task_type: "staff" }
          ]
        },
        {
          title: "Meeting",
          icon: "🤝",
          tasks: [
            { title: "Review performance metrics", task_type: "staff" },
            { title: "Discuss challenges", task_type: "staff" },
            { title: "Present growth opportunities", task_type: "staff" },
            { title: "Agree on next steps", task_type: "staff" }
          ]
        },
        {
          title: "Follow-up",
          icon: "✅",
          tasks: [
            { title: "Send meeting summary", task_type: "staff" },
            { title: "Update account plan", task_type: "staff" },
            { title: "Schedule follow-up actions", task_type: "staff" }
          ]
        }
      ]
    }),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: "user_123",
    updated_by: "user_123"
  },
  {
    id: generateUUID(),
    subscription_id: "sub_123456",
    name: "Renewal Process",
    description: "Standard process for client renewals",
    template_content: JSON.stringify({
      groups: [
        {
          title: "Pre-renewal",
          icon: "⏱️",
          tasks: [
            { title: "Renewal notification (90 days)", task_type: "automated" },
            { title: "Health check meeting", task_type: "staff" },
            { title: "Usage review", task_type: "staff" },
            { title: "Prepare renewal proposal", task_type: "staff" }
          ]
        },
        {
          title: "Renewal Discussion",
          icon: "💬",
          tasks: [
            { title: "Present renewal options", task_type: "staff" },
            { title: "Address concerns", task_type: "staff" },
            { title: "Negotiate terms", task_type: "staff" },
            { title: "Secure verbal commitment", task_type: "staff" }
          ]
        },
        {
          title: "Closing",
          icon: "📝",
          tasks: [
            { title: "Send renewal agreement", task_type: "staff" },
            { title: "Follow up on signature", task_type: "staff" },
            { title: "Process renewal", task_type: "automated" },
            { title: "Send welcome to new term", task_type: "staff" }
          ]
        }
      ]
    }),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: "user_123",
    updated_by: "user_123"
  }
]

// Mock Playbook Run
export const mockPlaybookRun: PlaybookRun = {
  id: "1",
  playbook_template_id: "1",
  subscription_id: "sub_123456",
  run_type: "on-demand",
  actual_start: new Date().toISOString(),
  target_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  status: "running",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  created_by: "user_123",
  updated_by: "user_123",
  
  // Additional fields for UI
  template: {
    id: "1",
    subscription_id: "sub_123456",
    name: "Client Onboarding",
    description: "Standard onboarding process for new clients",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: "user_123",
    updated_by: "user_123"
  },
  company_name: "WEISS & COMPANY",
  client_name: "John Weiss",
  company_logo: "",
  client_logo: "",
  subscription: mockSubscription,
  tasks: [
    {
      id: "task_1",
      playbook_run_id: "1",
      task_type: "task",
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Sign your contract",
      assignee: "John Weiss",
      assigneeAvatar: "",
      date: new Date().toISOString(),
      comments: 2
    },
    {
      id: "task_2",
      playbook_run_id: "1",
      task_type: "task",
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Schedule your kickoff call",
      description: "Invite anyone who will be using the product on a daily basis, so we can get alignment...",
      assignee: "Maria Silva",
      assigneeAvatar: "",
      date: new Date().toISOString(),
      comments: 5
    },
    {
      id: "task_3",
      playbook_run_id: "1",
      task_type: "task",
      status: "current",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Tell us more about your account setup",
      assignee: "John Weiss",
      assigneeAvatar: "",
      date: getFutureDate(2),
      comments: 3
    },
    {
      id: "task_4",
      playbook_run_id: "1",
      task_type: "task",
      status: "overdue",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Capture onboarding goals",
      description: "Take a moment to capture why you purchased so we can stay aligned throughout...",
      assignee: "Maria Silva",
      assigneeAvatar: "",
      date: getFutureDate(5),
      comments: 1
    },
    {
      id: "task_5",
      playbook_run_id: "1",
      task_type: "automated",
      status: "pending",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Complete account setup",
      assignee: "System",
      assigneeAvatar: "",
      date: getFutureDate(7),
      comments: 2
    },
    {
      id: "task_6",
      playbook_run_id: "1",
      task_type: "approval",
      status: "overdue",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Approve account configuration",
      description: "Review and approve the account configuration before proceeding to the next phase",
      assignee: "John Weiss",
      assigneeAvatar: "",
      date: getFutureDate(10),
      comments: 4
    },
    {
      id: "task_7",
      playbook_run_id: "1",
      task_type: "email_draft",
      status: "pending",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Draft welcome email",
      description: "Create a personalized welcome email to send to the client team",
      assignee: "Maria Silva",
      assigneeAvatar: "",
      date: getFutureDate(12),
      comments: 3
    },
    {
      id: "task_8",
      playbook_run_id: "1",
      task_type: "email_auto_send",
      status: "pending",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "user_123",
      updated_by: "user_123",
      title: "Send training materials",
      description: "Automatically send training materials to all users",
      assignee: "System",
      assigneeAvatar: "",
      date: getFutureDate(15),
      comments: 1
    }
  ]
};

// Mock Task Groups
export const mockTaskGroups: TaskGroup[] = [
  {
    id: "group_1",
    title: "Getting started",
    icon: "🎯",
    tasks: mockPlaybookRun.tasks?.slice(0, 5) || []
  },
  {
    id: "group_2",
    title: "Implementation",
    icon: "🔧",
    tasks: mockPlaybookRun.tasks?.slice(5) || []
  }
];

// Function to create a new playbook run from a template
export function createPlaybookRun(templateId: string, clientId: string, startDate: Date) {
  // Find the template
  const template = mockPlaybookTemplates.find(t => t.id === templateId);
  if (!template) {
    throw new Error(`Template with ID ${templateId} not found`);
  }
  
  // Find the client (in a real app, this would be from the database)
  const client = {
    id: clientId,
    name: `Client ${clientId}`,
    subscription_id: `sub_${clientId}`
  };
  
  // Create a new run ID
  const runId = crypto.randomUUID();
  
  // Parse template content to create tasks
  let tasks: PlaybookTask[] = [];
  if (template.template_content) {
    try {
      const content = JSON.parse(template.template_content);
      let taskOrder = 0;
      
      content.groups.forEach((group: any) => {
        group.tasks.forEach((task: any) => {
          tasks.push({
            id: crypto.randomUUID(),
            playbook_run_id: runId,
            task_type: task.task_type || "task",
            title: task.title,
            description: task.description || "",
            status: taskOrder === 0 ? "current" : "pending", // First task is current, others pending
            task_order: taskOrder++,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: "current_user",
            updated_by: "current_user"
          });
        });
      });
    } catch (error) {
      console.error("Error parsing template content:", error);
    }
  }
  
  // Create the new run
  const newRun: PlaybookRun = {
    id: runId,
    playbook_template_id: template.id,
    subscription_id: client.subscription_id,
    run_type: "on-demand",
    scheduled_start: startDate.toISOString(),
    actual_start: new Date().toISOString(),
    status: "running",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: "current_user",
    updated_by: "current_user",
    
    // Additional fields for UI
    template: template,
    company_name: "Company Name",
    client_name: client.name,
    tasks: tasks
  };
  
  // In a real app, this would save to the database
  // For our mock, we'll just return the new run
  return newRun;
} 